import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import type { ItemDoChecklist } from '@/modulos/operacao/comum/DocumentChecklist';
import { type ErroApi } from '@/nucleo/auth/cliente-api';

/**
 * O dado das abas da tela de detalhe do credenciado que ainda nao leem a API — mockado.
 *
 * Dados Gerais e Representantes ja leem a API (`dados.ts`, `useCredenciado` e
 * `useRepresentantesDoCredenciado`, lote L7 de 2026-10-03). As demais abas abaixo
 * continuam na excecao de 2026-08-27.
 *
 * Decisao do dono, 2026-08-27 (CLAUDE.md, "Proibido"; docs/ALVOS.md, "Cinco telas
 * de detalhe dos modulos de cadastro"): a excecao de mock de leitura que ja cobria
 * os cinco modulos de cadastro cresceu para cobrir tambem as abas que mostram dado
 * de OUTRA tabela — termos, ordens de servico, processos SEI, DAMSP, multas,
 * recursos administrativos, representantes, documentos e historico. Nenhuma dessas
 * tabelas tem rota na API hoje (`apps/api/src/modulos/` so tem `eu`, `govbr` e
 * `senha`), e a Fase 1 nao pode esperar por elas para validar layout com a SMT/DTP.
 *
 * Cada hook aqui e AUTOSSUFICIENTE: gera sua propria lista pequena (2 a 5 linhas),
 * sem consultar `dados.ts` nem as outras listagens do modulo — a mesma cautela que
 * a instrucao do alvo pediu para nao acoplar este arquivo ao array mutavel de outro
 * modulo em edicao simultanea (`veiculos/dados.ts`). So LEITURA: nenhuma funcao
 * grava nada, e as telas que abrem formulario mostram o aviso de indisponibilidade
 * em vez de fingir sucesso.
 */

function diasAtras(dias: number): string {
  return new Date(Date.now() - dias * 86_400_000).toISOString();
}

// ─── Termos ──────────────────────────────────────────────────────────────────

export interface TermoDoCredenciado {
  id: string;
  numero_termo: string | null;
  numero_sei: string | null;
  status: 'ATIVO' | 'ENCERRADO' | 'RESCINDIDO' | 'ASSINATURA_PENDENTE';
  data_inicio: string | null;
  data_fim: string | null;
  valor_mensal: number | null;
  saldo_referencia: number | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function termosMock(credenciadoId: string): TermoDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-termo-1`,
      numero_termo: 'TA-2024/0142',
      numero_sei: '6017.2024/0001234-5',
      status: 'ATIVO',
      data_inicio: diasAtras(320),
      data_fim: diasAtras(-410),
      valor_mensal: 48500.3,
      saldo_referencia: 620000,
    },
    {
      id: `${credenciadoId}-termo-2`,
      numero_termo: 'TA-2022/0087',
      numero_sei: '6017.2022/0000876-1',
      status: 'ENCERRADO',
      data_inicio: diasAtras(1050),
      data_fim: diasAtras(325),
      valor_mensal: 41200,
      saldo_referencia: 0,
    },
  ];
}

export function useTermosDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<TermoDoCredenciado[], ErroApi> {
  return useQuery<TermoDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'termos'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => termosMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Visão financeira ────────────────────────────────────────────────────────

export interface MedicaoDoCredenciado {
  id: string;
  numero_termo: string | null;
  mes_referencia: string;
  status:
    | 'RASCUNHO'
    | 'PROCESSADA'
    | 'APROVADA'
    | 'EXPORTADA_SOF'
    | 'EMPENHADA'
    | 'LIQUIDADA'
    | 'AUTORIZADA'
    | 'PAGA';
  valor_total: number;
}

export interface PerfilFinanceiroDoCredenciado {
  reserva_sof: number;
  empenhado: number;
  liquidado: number;
  autorizado: number;
  pago: number;
  saldo_disponivel: number | null;
  percentual_consumido: number | null;
  qtd_termos_ativos: number;
  medicoes: MedicaoDoCredenciado[];
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function perfilFinanceiroMock(credenciadoId: string): PerfilFinanceiroDoCredenciado {
  const reserva_sof = 620000;
  const empenhado = 388000;
  return {
    reserva_sof,
    empenhado,
    liquidado: 291000,
    autorizado: 242000,
    pago: 194000,
    saldo_disponivel: reserva_sof - empenhado,
    percentual_consumido: (empenhado / reserva_sof) * 100,
    qtd_termos_ativos: 1,
    medicoes: [
      {
        id: `${credenciadoId}-med-1`,
        numero_termo: 'TA-2024/0142',
        mes_referencia: '2026-07',
        status: 'PAGA',
        valor_total: 48500.3,
      },
      {
        id: `${credenciadoId}-med-2`,
        numero_termo: 'TA-2024/0142',
        mes_referencia: '2026-06',
        status: 'PAGA',
        valor_total: 48500.3,
      },
      {
        id: `${credenciadoId}-med-3`,
        numero_termo: 'TA-2024/0142',
        mes_referencia: '2026-05',
        status: 'AUTORIZADA',
        valor_total: 48500.3,
      },
      {
        id: `${credenciadoId}-med-4`,
        numero_termo: 'TA-2024/0142',
        mes_referencia: '2026-04',
        status: 'LIQUIDADA',
        valor_total: 48500.3,
      },
      {
        id: `${credenciadoId}-med-5`,
        numero_termo: 'TA-2024/0142',
        mes_referencia: '2026-03',
        status: 'PROCESSADA',
        valor_total: 48500.3,
      },
    ],
  };
}

export function usePerfilFinanceiroDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<PerfilFinanceiroDoCredenciado, ErroApi> {
  return useQuery<PerfilFinanceiroDoCredenciado, ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'perfil-financeiro'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => perfilFinanceiroMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Ordens de serviço ───────────────────────────────────────────────────────

export interface OrdemDeServicoDoCredenciado {
  id: string;
  numero: number;
  status: 'EM_EXECUCAO' | 'CONCLUIDA' | 'SUSPENSA' | 'CANCELADA';
  numero_sei_termo: string | null;
  data_emissao: string | null;
  data_inicio: string | null;
  data_fim: string | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function ordensDeServicoMock(credenciadoId: string): OrdemDeServicoDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-os-1`,
      numero: 1042,
      status: 'EM_EXECUCAO',
      numero_sei_termo: '6017.2024/0001234-5',
      data_emissao: diasAtras(90),
      data_inicio: diasAtras(85),
      data_fim: diasAtras(-190),
    },
    {
      id: `${credenciadoId}-os-2`,
      numero: 987,
      status: 'CONCLUIDA',
      numero_sei_termo: '6017.2022/0000876-1',
      data_emissao: diasAtras(400),
      data_inicio: diasAtras(395),
      data_fim: diasAtras(120),
    },
  ];
}

export function useOrdensDeServicoDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<OrdemDeServicoDoCredenciado[], ErroApi> {
  return useQuery<OrdemDeServicoDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'ordens-de-servico'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => ordensDeServicoMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Processos SEI ───────────────────────────────────────────────────────────

export interface ProcessoSeiDoCredenciado {
  id: string;
  numero_sei: string;
  tipo_processo: string;
  status_sei: 'EM_ANDAMENTO' | 'CONCLUIDO' | 'ARQUIVADO' | 'CANCELADO';
  data_abertura: string | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function processosSeiMock(credenciadoId: string): ProcessoSeiDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-sei-1`,
      numero_sei: '6017.2026/0000501-2',
      tipo_processo: 'FISCALIZACAO',
      status_sei: 'EM_ANDAMENTO',
      data_abertura: diasAtras(40),
    },
    {
      id: `${credenciadoId}-sei-2`,
      numero_sei: '6017.2024/0001234-5',
      tipo_processo: 'TERMO_ADESAO',
      status_sei: 'CONCLUIDO',
      data_abertura: diasAtras(330),
    },
  ];
}

export function useProcessosSeiDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<ProcessoSeiDoCredenciado[], ErroApi> {
  return useQuery<ProcessoSeiDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'processos-sei'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => processosSeiMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── DAMSP (cobranças administrativas) ───────────────────────────────────────

export interface CobrancaDamspDoCredenciado {
  id: string;
  numero_damsp: string | null;
  tipo_servico: string;
  descricao: string;
  data_vencimento: string | null;
  status_cobranca: 'PENDENTE' | 'EMITIDA' | 'PAGA' | 'VENCIDA' | 'CANCELADA';
  valor: number;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function damspMock(credenciadoId: string): CobrancaDamspDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-damsp-1`,
      numero_damsp: '2026/004521',
      tipo_servico: 'TAXA_VISTORIA',
      descricao: 'Taxa de vistoria anual da frota',
      data_vencimento: diasAtras(-15),
      status_cobranca: 'EMITIDA',
      valor: 1250,
    },
    {
      id: `${credenciadoId}-damsp-2`,
      numero_damsp: '2025/009873',
      tipo_servico: 'MULTA_ADMINISTRATIVA',
      descricao: 'Cobrança referente a multa aplicada',
      data_vencimento: diasAtras(60),
      status_cobranca: 'PAGA',
      valor: 3400,
    },
  ];
}

export function useDamspDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<CobrancaDamspDoCredenciado[], ErroApi> {
  return useQuery<CobrancaDamspDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'damsp'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => damspMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Multas ──────────────────────────────────────────────────────────────────

export interface MultaDoCredenciado {
  id: string;
  tipo_infracao: string;
  mes_referencia: string;
  percentual: number;
  status_multa: 'APLICADA' | 'RECORRIDA' | 'CANCELADA';
  valor_multa: number | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function multasMock(credenciadoId: string): MultaDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-multa-1`,
      tipo_infracao: 'ATRASO_NA_ROTA',
      mes_referencia: '2026-06',
      percentual: 2,
      status_multa: 'APLICADA',
      valor_multa: 970.01,
    },
    {
      id: `${credenciadoId}-multa-2`,
      tipo_infracao: 'VEICULO_SEM_VISTORIA',
      mes_referencia: '2026-02',
      percentual: 5,
      status_multa: 'RECORRIDA',
      valor_multa: 2425.02,
    },
  ];
}

export function useMultasDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<MultaDoCredenciado[], ErroApi> {
  return useQuery<MultaDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'multas'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => multasMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Recursos administrativos ────────────────────────────────────────────────

export interface RecursoDoCredenciado {
  id: string;
  tipo_recurso: string;
  descricao: string;
  data_protocolo: string | null;
  status_recurso: 'PROTOCOLADO' | 'EM_ANALISE' | 'DEFERIDO' | 'INDEFERIDO';
  decisao: string | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function recursosMock(credenciadoId: string): RecursoDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-recurso-1`,
      tipo_recurso: 'RECURSO_DE_MULTA',
      descricao: 'Contestação da multa por atraso na rota de junho',
      data_protocolo: diasAtras(20),
      status_recurso: 'EM_ANALISE',
      decisao: null,
    },
    {
      id: `${credenciadoId}-recurso-2`,
      tipo_recurso: 'RECURSO_DE_MULTA',
      descricao: 'Contestação da multa por veículo sem vistoria',
      data_protocolo: diasAtras(200),
      status_recurso: 'INDEFERIDO',
      decisao: 'Mantida a multa — vistoria vencida confirmada no laudo.',
    },
  ];
}

export function useRecursosDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<RecursoDoCredenciado[], ErroApi> {
  return useQuery<RecursoDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'recursos'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => recursosMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Frota (sub-aba "Veículos" de Frota & Equipe) ────────────────────────────

export interface VeiculoDaFrotaDoCredenciado {
  id: string;
  slug: string;
  placa: string;
  marca: string | null;
  modelo: string | null;
  ano_fabricacao: number | null;
  tipo_bancada: string | null;
  ja_inscrito: boolean;
  status: 'ATIVO' | 'INATIVO' | 'SUSPENSO';
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Slugs no mesmo padrao de `veiculos/dados.ts` (nao importado daqui de proposito —
// ver o doc-comment do topo do arquivo).
function frotaMock(credenciadoId: string): VeiculoDaFrotaDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-veiculo-1`,
      slug: 'veiculo-exemplo-1',
      placa: 'EXP1A23',
      marca: 'Marca Exemplo',
      modelo: 'Van Exemplo',
      ano_fabricacao: 2022,
      tipo_bancada: 'VAN',
      ja_inscrito: true,
      status: 'ATIVO',
    },
    {
      id: `${credenciadoId}-veiculo-2`,
      slug: 'veiculo-exemplo-2',
      placa: 'EXP2B45',
      marca: 'Marca Exemplo',
      modelo: 'Micro-ônibus Exemplo',
      ano_fabricacao: 2019,
      tipo_bancada: 'MICRO_ONIBUS',
      ja_inscrito: false,
      status: 'ATIVO',
    },
  ];
}

export function useFrotaDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<VeiculoDaFrotaDoCredenciado[], ErroApi> {
  return useQuery<VeiculoDaFrotaDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'frota'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => frotaMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Vínculos (sub-abas "Condutores"/"Monitores" de Frota & Equipe) ──────────

export interface VinculoDoCredenciado {
  id: string;
  pessoa_nome: string;
  pessoa_slug: string;
  tipo_pessoa_vinculada: 'CONDUTOR' | 'MONITOR';
  tipo_vinculo: 'CLT' | 'AUTONOMO' | 'MEI' | 'SOCIO';
  data_admissao: string | null;
  status_vinculo: 'ATIVO' | 'PENDENTE' | 'INATIVO';
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Slugs no mesmo padrao de `condutores/dados.ts` e `monitores/dados.ts` (nao
// importados daqui de proposito — ver o doc-comment do topo do arquivo).
function vinculosMock(credenciadoId: string): VinculoDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-vinculo-1`,
      pessoa_nome: 'Condutor Exemplo da Silva',
      pessoa_slug: 'condutor-exemplo-1',
      tipo_pessoa_vinculada: 'CONDUTOR',
      tipo_vinculo: 'CLT',
      data_admissao: diasAtras(500),
      status_vinculo: 'ATIVO',
    },
    {
      id: `${credenciadoId}-vinculo-2`,
      pessoa_nome: 'Condutor Exemplo Souza',
      pessoa_slug: 'condutor-exemplo-2',
      tipo_pessoa_vinculada: 'CONDUTOR',
      tipo_vinculo: 'AUTONOMO',
      data_admissao: diasAtras(90),
      status_vinculo: 'PENDENTE',
    },
    {
      id: `${credenciadoId}-vinculo-3`,
      pessoa_nome: 'Monitora Exemplo Pereira',
      pessoa_slug: 'monitor-exemplo-1',
      tipo_pessoa_vinculada: 'MONITOR',
      tipo_vinculo: 'CLT',
      data_admissao: diasAtras(300),
      status_vinculo: 'ATIVO',
    },
  ];
}

export function useVinculosDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<VinculoDoCredenciado[], ErroApi> {
  return useQuery<VinculoDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'vinculos'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => vinculosMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Documentos ──────────────────────────────────────────────────────────────

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function documentosMock(credenciadoId: string): ItemDoChecklist[] {
  return [
    {
      id: `${credenciadoId}-doc-1`,
      nomeDocumento: 'CNPJ / Contrato social',
      obrigatorio: true,
      status: 'aprovado',
      documento: { arquivoNome: 'contrato_social.pdf' },
    },
    {
      id: `${credenciadoId}-doc-2`,
      nomeDocumento: 'Comprovante de residência',
      obrigatorio: true,
      status: 'aprovado',
      documento: { arquivoNome: 'comprovante.pdf' },
    },
    {
      id: `${credenciadoId}-doc-3`,
      nomeDocumento: 'CND Municipal',
      obrigatorio: true,
      status: 'enviado',
    },
    {
      id: `${credenciadoId}-doc-4`,
      nomeDocumento: 'CND Federal',
      obrigatorio: true,
      status: 'pendente',
    },
    {
      id: `${credenciadoId}-doc-5`,
      nomeDocumento: 'CRF FGTS',
      obrigatorio: true,
      status: 'rejeitado',
      descricao: 'Certidão vencida — reenviar versão atualizada.',
    },
    {
      id: `${credenciadoId}-doc-6`,
      nomeDocumento: 'CADIN Anexo III',
      obrigatorio: true,
      status: 'pendente',
    },
    {
      id: `${credenciadoId}-doc-7`,
      nomeDocumento: 'Anexo VII — comprovação operacional',
      obrigatorio: false,
      status: 'aprovado',
      documento: { arquivoNome: 'anexo_vii.pdf' },
    },
  ];
}

export function useDocumentosDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<ItemDoChecklist[], ErroApi> {
  return useQuery<ItemDoChecklist[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'documentos'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => documentosMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Suspensões cautelares ───────────────────────────────────────────────────

export interface SuspensaoCautelarDoCredenciado {
  id: string;
  status: 'ATIVA' | 'LEVANTADA';
  motivo: string;
  motivo_detalhe: string | null;
  data_inicio: string;
  data_fim_prevista: string | null;
  processo_sei: string | null;
  levantamento_motivo: string | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function suspensoesMock(
  credenciadoId: string,
  statusDoCredenciado: string,
): SuspensaoCautelarDoCredenciado[] {
  const ativa = statusDoCredenciado === 'SUSPENSO' || statusDoCredenciado === 'BLOQUEADO';
  const lista: SuspensaoCautelarDoCredenciado[] = [
    {
      id: `${credenciadoId}-suspensao-1`,
      status: 'LEVANTADA',
      motivo: 'DOC_VENCIDA',
      motivo_detalhe: 'CRF FGTS vencido por 12 dias.',
      data_inicio: diasAtras(260),
      data_fim_prevista: diasAtras(230),
      processo_sei: null,
      levantamento_motivo: 'Documentação regularizada.',
    },
  ];
  if (ativa) {
    lista.unshift({
      id: `${credenciadoId}-suspensao-2`,
      status: 'ATIVA',
      motivo: 'INVESTIGACAO_OCORRENCIA',
      motivo_detalhe: 'Ocorrência grave relatada por escola atendida.',
      data_inicio: diasAtras(20),
      data_fim_prevista: null,
      processo_sei: '6017.2026/0000501-2',
      levantamento_motivo: null,
    });
  }
  return lista;
}

export function useSuspensoesDoCredenciado(
  credenciadoId: string | undefined,
  statusDoCredenciado: string | undefined,
): UseQueryResult<SuspensaoCautelarDoCredenciado[], ErroApi> {
  return useQuery<SuspensaoCautelarDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'suspensoes'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => suspensoesMock(credenciadoId!, statusDoCredenciado ?? ''),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Histórico ───────────────────────────────────────────────────────────────

export interface EventoDeHistoricoDoCredenciado {
  id: string;
  acao: 'INSERT' | 'UPDATE' | 'DELETE';
  campos_alterados: string[];
  created_at: string;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function historicoMock(credenciadoId: string): EventoDeHistoricoDoCredenciado[] {
  return [
    {
      id: `${credenciadoId}-hist-1`,
      acao: 'UPDATE',
      campos_alterados: ['telefone', 'email'],
      created_at: diasAtras(5),
    },
    {
      id: `${credenciadoId}-hist-2`,
      acao: 'UPDATE',
      campos_alterados: ['status'],
      created_at: diasAtras(45),
    },
    {
      id: `${credenciadoId}-hist-3`,
      acao: 'INSERT',
      campos_alterados: [],
      created_at: diasAtras(400),
    },
  ];
}

export function useHistoricoDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<EventoDeHistoricoDoCredenciado[], ErroApi> {
  return useQuery<EventoDeHistoricoDoCredenciado[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'historico'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => historicoMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

/** O que o modal "Ver histórico de inscrição" mostra — versão enxuta do D12 do protótipo. */
export interface HistoricoDeInscricaoDoCredenciado {
  protocolo: string;
  status_inscricao: string;
  data_envio: string | null;
  data_analise: string | null;
  parecer: string | null;
  eventos: EventoDeHistoricoDoCredenciado[];
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function historicoDeInscricaoMock(credenciadoId: string): HistoricoDeInscricaoDoCredenciado {
  return {
    protocolo: `INSC-${credenciadoId.slice(-4).toUpperCase()}`,
    status_inscricao: 'APROVADA',
    data_envio: diasAtras(470),
    data_analise: diasAtras(440),
    parecer: 'Documentação conforme o edital. Inscrição deferida.',
    eventos: [
      {
        id: `${credenciadoId}-insc-hist-1`,
        acao: 'UPDATE',
        campos_alterados: ['status_inscricao'],
        created_at: diasAtras(440),
      },
      {
        id: `${credenciadoId}-insc-hist-2`,
        acao: 'INSERT',
        campos_alterados: [],
        created_at: diasAtras(470),
      },
    ],
  };
}

export function useHistoricoDeInscricaoDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<HistoricoDeInscricaoDoCredenciado, ErroApi> {
  return useQuery<HistoricoDeInscricaoDoCredenciado, ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'historico-inscricao'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => historicoDeInscricaoMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Linha do tempo ──────────────────────────────────────────────────────────

export interface EventoDaLinhaDoTempo {
  id: string;
  tipo:
    | 'INSCRICAO'
    | 'TERMO'
    | 'OS'
    | 'MULTA'
    | 'OCORRENCIA'
    | 'RECURSO'
    | 'SUSPENSAO'
    | 'DESCREDENCIAMENTO';
  titulo: string;
  descricao: string | null;
  data_evento: string;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function linhaDoTempoMock(credenciadoId: string): EventoDaLinhaDoTempo[] {
  return [
    {
      id: `${credenciadoId}-linha-1`,
      tipo: 'OS',
      titulo: 'Ordem de serviço OS-1042 emitida',
      descricao: null,
      data_evento: diasAtras(90),
    },
    {
      id: `${credenciadoId}-linha-2`,
      tipo: 'TERMO',
      titulo: 'Termo de adesão TA-2024/0142 assinado',
      descricao: null,
      data_evento: diasAtras(320),
    },
    {
      id: `${credenciadoId}-linha-3`,
      tipo: 'INSCRICAO',
      titulo: 'Inscrição aprovada pelo DTP',
      descricao: 'Documentação conforme o edital.',
      data_evento: diasAtras(440),
    },
    {
      id: `${credenciadoId}-linha-4`,
      tipo: 'INSCRICAO',
      titulo: 'Inscrição enviada pelo portal',
      descricao: null,
      data_evento: diasAtras(470),
    },
  ];
}

export function useLinhaDoTempoDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<EventoDaLinhaDoTempo[], ErroApi> {
  return useQuery<EventoDaLinhaDoTempo[], ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'linha-do-tempo'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => linhaDoTempoMock(credenciadoId!),
    retry: naoInsistirEmRecusa,
  });
}

// ─── Gating (situação operacional) ───────────────────────────────────────────

export interface GatingDoCredenciado {
  pode_operar: boolean;
  motivos: string[];
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
function gatingMock(statusDoCredenciado: string): GatingDoCredenciado {
  if (statusDoCredenciado === 'SUSPENSO' || statusDoCredenciado === 'BLOQUEADO') {
    return { pode_operar: false, motivos: ['Credenciado com situação cadastral irregular.'] };
  }
  return { pode_operar: true, motivos: [] };
}

export function useGatingDoCredenciado(
  credenciadoId: string | undefined,
  statusDoCredenciado: string | undefined,
): UseQueryResult<GatingDoCredenciado, ErroApi> {
  return useQuery<GatingDoCredenciado, ErroApi>({
    queryKey: ['credenciados', 'detalhe', credenciadoId, 'gating'],
    enabled: !!credenciadoId,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => gatingMock(statusDoCredenciado ?? ''),
    retry: naoInsistirEmRecusa,
  });
}
